import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ArticleCard from '../components/ArticleCard';
import { configAPI, preferencesAPI, articlesAPI, libraryAPI } from '../api';
import { toast } from 'sonner';
import { Search, Filter, X, ArrowLeft, BookmarkPlus, Loader2 } from 'lucide-react';

const ExplorePage = () => {
  const simpleUIEnabled = useFeatureFlag('enable_explore_simple_pubmed_ui');
  
  // Render simple UI if flag is enabled
  if (simpleUIEnabled) {
    return <SimpleExploreUI />;
  }
  
  // Original complex UI
  return <FullExploreUI />;
};

// ============================================================================
// SIMPLE EXPLORE UI (Phase UX-B)
// Minimal PubMed search without date controls or topic suggestions
// ============================================================================
const SimpleExploreUI = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [savingPmid, setSavingPmid] = useState(null);

  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    setResults([]);
    try {
      // Use search-v2 with use_preferences_context=false for simple search
      const response = await articlesAPI.searchV2({
        query,
        use_preferences_context: false,
        limit: 25,
      });
      setResults(response.articles || []);
      if (response.articles?.length === 0) {
        toast.info('No articles found. Try different search terms.');
      } else {
        toast.success(`Found ${response.articles.length} articles`);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.detail?.message || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToLibrary = async (pmid) => {
    if (!pmid) return;
    setSavingPmid(pmid);
    try {
      await libraryAPI.saveArticle(pmid);
      toast.success('Article saved to library');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('Article is already in your library');
      } else {
        toast.error('Failed to save article');
      }
    } finally {
      setSavingPmid(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="explore-simple-ui">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/home')} data-testid="explore-back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Search PubMed</h1>
          <p className="text-gray-600 text-sm">Find medical literature with a simple search</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Search for articles (e.g., diabetes management, stroke prevention)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="explore-simple-search-input"
                  disabled={loading}
                />
              </div>
              <Button 
                onClick={handleSearch} 
                disabled={loading || !searchQuery.trim()}
                data-testid="explore-simple-search-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Loader2 className="h-8 w-8 text-blue-500 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">Searching PubMed...</p>
            </CardContent>
          </Card>
        ) : results.length > 0 ? (
          <div data-testid="explore-simple-results">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {results.length} Result{results.length !== 1 ? 's' : ''}
              </h2>
            </div>
            <div className="space-y-4">
              {results.map((article) => (
                <Card key={article.pmid} className="hover:shadow-md transition-shadow" data-testid={`explore-result-${article.pmid}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="responsive-title text-base font-semibold text-gray-900 mb-1 line-clamp-2 cursor-pointer hover:text-blue-600"
                          onClick={() => navigate(`/article/${article.pmid}`)}
                        >
                          {article.title}
                        </h3>
                        <p className="text-sm text-blue-600 mb-2">{article.journal}</p>
                        {article.ai_summary && (
                          <p className="text-sm text-gray-600 line-clamp-2">{article.ai_summary}</p>
                        )}
                        {article.design_tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {article.design_tags.slice(0, 3).map((tag, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSaveToLibrary(article.pmid)}
                        disabled={savingPmid === article.pmid}
                        data-testid={`save-article-${article.pmid}`}
                      >
                        {savingPmid === article.pmid ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <BookmarkPlus className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ) : searchQuery && !loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Results Found</h3>
              <p className="text-gray-600">Try different search terms</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Search PubMed</h3>
              <p className="text-gray-600 mb-4">
                Enter keywords to find medical literature
              </p>
              <p className="text-sm text-gray-500">
                Example: "heart failure treatment", "COVID-19 vaccines"
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// FULL EXPLORE UI (Original)
// Complex search with filters, topic suggestions, date ranges
// ============================================================================
const FullExploreUI = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [specialtyConfig, setSpecialtyConfig] = useState(null);
  
  // Filters
  const [dateRange, setDateRange] = useState('30');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedJournals, setSelectedJournals] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  const designTypes = [
    'Randomized Controlled Trial',
    'Meta-Analysis',
    'Systematic Review',
    'Cohort Study',
    'Case-Control Study',
    'Clinical Trial',
    'Review',
    'Case Report'
  ];

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const config = await configAPI.getSpecialties();
      setSpecialtyConfig(config);
      
      const prefs = await preferencesAPI.getMyPreferences();
      setPreferences(prefs);
    } catch (error) {
      console.log('Could not load preferences');
    }
  };

  const getAvailableTopics = () => {
    if (!preferences || !specialtyConfig) return [];
    
    const specialty = specialtyConfig.specialties.find(s => s.id === preferences.specialty_id);
    if (!specialty) return [];
    
    const subspecialty = specialty.subspecialties.find(sub => sub.id === preferences.subspecialty_id);
    if (!subspecialty) return [];
    
    return subspecialty.topics || [];
  };

  const getAvailableJournals = () => {
    if (!preferences || !specialtyConfig) return [];
    
    const specialty = specialtyConfig.specialties.find(s => s.id === preferences.specialty_id);
    if (!specialty) return [];
    
    const subspecialty = specialty.subspecialties.find(sub => sub.id === preferences.subspecialty_id);
    if (!subspecialty) return [];
    
    return (subspecialty.journals || []).map(j => typeof j === 'string' ? j : j.name);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    try {
      setLoading(true);
      setResults([]);
      
      const filters = {
        date_range_days: parseInt(dateRange),
        topics: selectedTopics.length > 0 ? selectedTopics : undefined,
        journals: selectedJournals.length > 0 ? selectedJournals : undefined,
        study_designs: selectedDesigns.length > 0 ? selectedDesigns : undefined
      };
      
      const response = await articlesAPI.search(searchQuery, filters);
      
      setResults(response.articles || []);
      
      if (response.articles.length === 0) {
        toast.info('No articles found. Try adjusting your search or filters.');
      } else {
        toast.success(`Found ${response.articles.length} articles`);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = (topic) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter(t => t !== topic));
    } else {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  const toggleJournal = (journal) => {
    if (selectedJournals.includes(journal)) {
      setSelectedJournals(selectedJournals.filter(j => j !== journal));
    } else {
      setSelectedJournals([...selectedJournals, journal]);
    }
  };

  const toggleDesign = (design) => {
    if (selectedDesigns.includes(design)) {
      setSelectedDesigns(selectedDesigns.filter(d => d !== design));
    } else {
      setSelectedDesigns([...selectedDesigns, design]);
    }
  };

  const clearFilters = () => {
    setSelectedTopics([]);
    setSelectedJournals([]);
    setSelectedDesigns([]);
    setDateRange('30');
  };

  const activeFilterCount = selectedTopics.length + selectedJournals.length + selectedDesigns.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/home')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Explore Literature</h1>
          <p className="text-gray-600">Search PubMed for medical literature</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Enter search terms (e.g., heart failure management)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button onClick={handleSearch} disabled={loading}>
                <Search className="h-4 w-4 mr-2" />
                {loading ? 'Searching...' : 'Search'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge className="ml-2" variant="secondary">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Filters</CardTitle>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Range */}
              <div>
                <label className="text-sm font-semibold text-gray-900 mb-2 block">Date Range</label>
                <div className="flex gap-2">
                  {['7', '30', '90', '365'].map((days) => (
                    <Button
                      key={days}
                      variant={dateRange === days ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDateRange(days)}
                    >
                      {days === '365' ? '1 Year' : `${days} Days`}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Topics */}
              {getAvailableTopics().length > 0 && (
                <div>
                  <label className="text-sm font-semibold text-gray-900 mb-2 block">Topics</label>
                  <div className="flex flex-wrap gap-2">
                    {getAvailableTopics().map((topic) => (
                      <Badge
                        key={topic}
                        variant={selectedTopics.includes(topic) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleTopic(topic)}
                      >
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Journals */}
              {getAvailableJournals().length > 0 && (
                <div>
                  <label className="text-sm font-semibold text-gray-900 mb-2 block">Journals</label>
                  <div className="flex flex-wrap gap-2">
                    {getAvailableJournals().map((journal) => (
                      <Badge
                        key={journal}
                        variant={selectedJournals.includes(journal) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleJournal(journal)}
                      >
                        {journal}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Study Design */}
              <div>
                <label className="text-sm font-semibold text-gray-900 mb-2 block">Study Design</label>
                <div className="flex flex-wrap gap-2">
                  {designTypes.map((design) => (
                    <Badge
                      key={design}
                      variant={selectedDesigns.includes(design) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleDesign(design)}
                    >
                      {design}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {results.length > 0 ? (
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {results.length} Result{results.length !== 1 ? 's' : ''}
              </h2>
            </div>
            <div className="space-y-4">
              {results.map((article) => (
                <ArticleCard
                  key={article.pmid}
                  article={article}
                  showFeedback={true}
                  context="explore"
                />
              ))}
            </div>
          </div>
        ) : !loading && searchQuery && (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Results Found</h3>
              <p className="text-gray-600">Try adjusting your search terms or filters</p>
            </CardContent>
          </Card>
        )}

        {!searchQuery && !loading && (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Search PubMed</h3>
              <p className="text-gray-600 mb-4">
                Enter keywords to search for medical literature
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExplorePage;